import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { FormHelperText } from '@mui/material'

import { LoadFields } from './LoadFields'

import type { DynamicLoadFieldValue, ExecutionEventsFormValues } from '../../../typings'

export type DynamicLoadsFieldListValue = ExecutionEventsFormValues['dynamicLoads']
const defaultComponentValue: DynamicLoadsFieldListValue = {}

export type DynamicLoadsFieldListProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = DynamicLoadsFieldListValue,
> = TidalFormBridgeOptions<DynamicLoadsFieldListValue, TargetField, State, AcceptedFormValue>

export function DynamicLoadsField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = DynamicLoadsFieldListValue,
>(props: DynamicLoadsFieldListProps<TargetField, State, AcceptedFormValue>) {
  const {
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<DynamicLoadsFieldListValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const handleOnChange = (key: string, loadValue: number) => {
    onChange({ ...value, [key]: { ...value?.[key], value: loadValue } as DynamicLoadFieldValue })
  }

  return (
    <>
      <LoadFields
        onChange={handleOnChange}
        value={value}
        testId="dynamic-loads"
        fieldDisabled={fieldDisabled}
      />
      {showErrorAccent && fieldHasError && (
        <FormHelperText error>{errorOrHelperText}</FormHelperText>
      )}
    </>
  )
}
